import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";
import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  TextField,
  useMediaQuery,
  IconButton,
  Tab,
  Tabs,
  Divider,
  alpha,
  Grid,
  List,
  ListItem,
  Chip,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import MoreToCome from "./MoreToCome";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { marked } from "marked";
import { beautifyGradeText } from "./functions";
import LoadingDialog from "./LoadingDialog";

import { Dialog, DialogContent, Popover } from "@mui/material";
import { getStyles } from "../index";
import TableChartIcon from "@mui/icons-material/TableChart";
import ConfirmationDialog from "./ConfirmationDialog";
import ExportButton from "./ExportButton";

import Logger from "./Logger";
import { useSnackbar } from "./SnackbarHandler";
import AssessmentDisplay from "./AssessmentDisplay";

import { People, Description } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WelcomeDialogLessons from "./WelcomeDialogLessons";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import Quill from "./Quill";

import SchoolIcon from "@mui/icons-material/School";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SegmentIcon from "@mui/icons-material/Segment";
import LanguageIcon from "@mui/icons-material/Language";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import EssayListDrawer from "./EssayListDrawer";

import AssessmentStyleSelector from "./AssessmentStyleSelector";
import CreateAssessmentStyleDialog from "./CreateAssessmentStyleDialog";
import { callApi } from "./api";
import { trackLesson, checkLessonLimit } from "./functions";
import { setDoc, onSnapshot } from "firebase/firestore";

const EssayResult = ({ user, setMinimized }) => {
  const { docId } = useParams();
  const [essayData, setEssayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingReviewState, setLoadingReviewState] = useState(false);
  const [activeContentTab, setActiveContentTab] = useState(0);

  const [editing, setEditing] = useState(false);
  const [editedResults, setEditedResults] = useState("");

  const [activeTab, setActiveTab] = useState(0);
  const [reviewed, setReviewed] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const printContentRef = useRef(null);
  const theme = useTheme();
  const styles = getStyles(theme);
  const [showOriginalText, setShowOriginalText] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [quillContent, setQuillContent] = useState("");
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 3. Add this useEffect after your other useEffects:
  useEffect(() => {
    setMinimized(true);
    console.log("ess ", essayData);
    if (essayData?.results) {
      setQuillContent(essayData.results);
    }
  }, [essayData?.results]);

  // Lägg till dessa states i EssayResult komponenten
  const [showReassessment, setShowReassessment] = useState(false);
  const [newAssessmentStyle, setNewAssessmentStyle] = useState("balanced");
  const [customAssessmentStyles, setCustomAssessmentStyles] = useState([]);
  const [isCreateStyleDialogOpen, setIsCreateStyleDialogOpen] = useState(false);
  const [isReassessing, setIsReassessing] = useState(false);

  // Lägg till useEffect för att hämta custom assessment styles
  useEffect(() => {
    const fetchCustomAssessmentStyles = async () => {
      try {
        const stylesCollection = collection(db, "assessmentStyles");
        const q = query(stylesCollection, where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const stylesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCustomAssessmentStyles(stylesData);
      } catch (error) {
        console.error("Error fetching custom assessment styles:", error);
        showSnackbar(
          "Ett fel uppstod vid hämtning av bedömningsstilar",
          "error",
        );
      }
    };

    if (user?.uid) {
      fetchCustomAssessmentStyles();
    }
  }, [user?.uid]);

  // Lägg till dessa funktioner
  const handleDeleteStyle = async (styleId, event) => {
    event.stopPropagation();

    try {
      await deleteDoc(doc(db, "assessmentStyles", styleId));
      setCustomAssessmentStyles((prev) =>
        prev.filter((style) => style.id !== styleId),
      );

      if (newAssessmentStyle === styleId) {
        setNewAssessmentStyle("balanced");
      }

      showSnackbar("Bedömningsstil raderad", "success");
    } catch (error) {
      console.error("Error deleting assessment style:", error);
      showSnackbar("Ett fel uppstod vid radering av bedömningsstil", "error");
    }
  };

  const handleStyleCreated = (newStyle) => {
    setCustomAssessmentStyles((prev) => [...prev, newStyle]);
    setNewAssessmentStyle(newStyle.id);
    setIsCreateStyleDialogOpen(false);
  };

  const getAssessmentStyleInstruction = (style) => {
    const customStyle = customAssessmentStyles.find((s) => s.id === style);
    if (customStyle) {
      return `Bedömningsstil: ${customStyle.description}`;
    }

    switch (style) {
      case "encouraging":
        return `Bedömningsstil: Bedöm texten med en uppmuntrande och stödjande ton. Fokusera på textens styrkor och positiva aspekter. Ge konstruktiv feedback som motiverar eleven att utvecklas vidare. Var generös med beröm för goda insatser och formulera förbättringsförslag som uppmuntran snarare än kritik.`;
      case "critical":
        return `Bedömningsstil: Bedöm texten med höga akademiska krav och noggrann granskning. Var tydlig med brister och utvecklingsområden. Fokusera på vad som saknas för att nå högre betygsnivåer. Håll en konstruktiv men kravställande ton som utmanar eleven att prestera bättre.`;
      case "extra_encouraging":
        return `Bedömningsstil: Bedöm texten med en mycket uppmuntrande och motiverande ton. Fokusera starkt på textens styrkor och framsteg. Var extra generös med beröm och positiv feedback. Formulera all kritik som konstruktiva möjligheter och framtida potential. Använd språk som inspirerar och bygger självförtroende. Betona alltid vad eleven gör bra innan du föreslår förbättringar.`;
      case "extra_critical":
        return `Bedömningsstil: Bedöm texten med mycket höga akademiska krav och extra noggrann granskning. Var tydlig och specifik med alla brister och utvecklingsområden. Fokusera på vad som behöver förbättras för att nå excellens. Håll en strikt, akademisk ton som ställer höga krav på precision, argumentation och språklig korrekthet. Acceptera inte halvmesyrer och var tydlig med var standarden inte uppnås.`;
      default: // balanced
        return `Bedömningsstil: Bedöm texten objektivt enligt bedömningsmatrisen. Ge en rättvis analys av både styrkor och utvecklingsområden. Håll en neutral, professionell ton som är vägledande utan att vara överdrivet kritisk eller uppmuntrande.`;
    }
  };

  const handleReassessment = async () => {
    if (docId === "Fd0Tga66MLf1TsRv1xid") {
      showSnackbar("Demo-bedömning kan inte bedömas om.", "warning");
      return;
    }

    if (!user.isPremium) {
      showSnackbar(
        "Du måste vara premiummedlem för att använda denna funktion",
        "info",
      );
      return;
    }

    const canGenerate = await checkLessonLimit(user.uid);
    if (!canGenerate) {
      Logger.log("throws error here for user id ", user.uid);
      throw new Error("LESSON_LIMIT_REACHED_IN_CALL_API"); // This is all we need - just return null when limit is hit
    }

    setIsReassessing(true);

    try {
      await trackLesson(user.uid, "EssayReassessment.js", null, null);

      // Hämta rubrikdata
      let selectedRubricObject;
      try {
        const rubricRef = doc(db, "rubrics", essayData.rubricId);
        const rubricSnap = await getDoc(rubricRef);
        if (rubricSnap.exists()) {
          selectedRubricObject = rubricSnap.data();
        } else {
          throw new Error("Bedömningsmatrisen hittades inte");
        }
      } catch (error) {
        console.error("Error fetching rubric:", error);
        showSnackbar("Kunde inte hämta bedömningsmatrisen", "error");
        setIsReassessing(false);
        return;
      }

      const gradeInstructionsText = essayData.educationLevel; // Använd den ursprungliga årskursen

      const requestId = `essay_reassess_${user.uid}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

      const prompt = `Bedöm följande text baserat på den angivna bedömningsmatrisen. 
    Skriv bedömningen på ${essayData.language}. 
    Texten inom <h2>-taggar och <h3>-taggar nedan ska också vara på ${essayData.language}.
    Årskurs: ${gradeInstructionsText}.
    ${getAssessmentStyleInstruction(newAssessmentStyle)}

    ${
      selectedRubricObject.assignmentName ||
      selectedRubricObject.assignmentDescription
        ? `Uppgiftsinformation:
    ${selectedRubricObject.assignmentName ? `Uppgiftens namn: ${selectedRubricObject.assignmentName}` : ""}
    ${selectedRubricObject.assignmentDescription ? `Uppgiftsbeskrivning: ${selectedRubricObject.assignmentDescription}` : ""}
      `
        : ""
    }
        
    Ge en analys för varje kriterium, förslag på förbättringar och ett betyg. Formatera svaret med följande struktur:

    VIKTIGT: Du MÅSTE inkludera två delar i ditt svar:
    
    1. Först den vanliga bedömningen
    2. Därefter, MÅSTE du inkludera en scores-sektion i slutet, som baseras på bedömningsmatrisens krav för varje betyg, med exakt detta format:
    
    <scores>
    {
      "criteriaScores": {
        "${selectedRubricObject.criteria[0].name}": {
          "score": 4,
          "grade": "A",
          "motivation": "Konkret motivering på exakt 2 meningar"
        }
        // Repetera för varje kriterium
      },
      "overallScore": {
        "score": 4,
        "grade": "A",
        "motivation": "Konkret motivering på exakt 4 meningar"
      }
    }
    </scores>
    
    Notera: scores-sektionen MÅSTE vara med i ditt svar och MÅSTE innehålla ett score mellan 0-5 för varje kriterium samt ett totalbetyg.
    
    <h2>Kriterieanalys</h2>
    
    <h3>[Kriteriumets namn]</h3>
    <p><strong>Analys:</strong> [Analys av detta kriterium på 3-4 meningar]</p>
    
    <blockquote><p><strong>Förbättringsförslag:</strong>[Förslag på förbättring på 3-4 meningar]</p></blockquote>
    
    <p><strong>Betyg:</strong> [Betyg för detta kriterium (A-F)]</p>
    [Upprepa för varje kriterium]
    
    <h3>Övergripande betyg</h3>
    <p>[Ange övergripande betyg här]</p>
    
    <hr>
    
    <h2>Bedömningsresultat</h2>
    
    <h3>Övergripande analys</h3>
    <p>[Ange övergripande analys av texten här]</p>
    
    <h3>Positiv feedback</h3>
    <p>[Ange positiv feedback om textens styrkor här]</p>
    
    <h3>Förbättringsplan</h3>
    <ul>
      <li>[Förbättringsåtgärd 1]</li>
      <li>[Förbättringsåtgärd 2]</li>
      <li>[Förbättringsåtgärd 3]</li>
      <li>[Förbättringsåtgärd 4]</li>
      <li>[Förbättringsåtgärd 5]</li>
    </ul>
    
    <hr>
    
    <h2>Felrapport</h2>
    
    <h3>Grammatik</h3>
    <p>[Lista med grammatikfel och förslag på korrigeringar]</p>
    
    <h3>Meningsbyggnad</h3>
    <p>[Beskrivning av problem med meningsbyggnad]</p>
    
    <h3>Sammanhangsproblem</h3>
    <p>[Identifierade problem med textens sammanhang]</p>
    
    <h3>Tydlighetsproblem</h3>
    <p>[Områden där texten kan förbättras för ökad tydlighet]</p>
    
    <h3>Logiska inkonsekvenser</h3>
    <p>[Identifierade logiska problem eller motsägelser i texten]</p>
    
    <h3>Stavfel</h3>
    <p>[Lista med stavfel och förslag på korrigeringar]</p>

    Bedömningsmatris:
    ${JSON.stringify(selectedRubricObject)}
    
    Text att bedöma:
    ${essayData.content}`;

      // Skapa en Firebase-förfrågan för att spåra denna begäran
      const requestRef = doc(db, "apiRequests", requestId);
      await setDoc(requestRef, {
        userId: user.uid,
        status: "pending",
        prompt: prompt,
        createdAt: new Date(),
      });

      // Sätt upp en lyssnare för Firebase-dokumentet
      const unsubscribe = onSnapshot(requestRef, async (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data();

        if (data.status === "completed" && data.response) {
          try {
            const responseData = data.response;
            let messageContent = responseData.choices[0].message.content.trim();

            // Extrahera scores före cleanup av markdown
            const extractScores = (content) => {
              try {
                const scoresMatch = content.match(
                  /<scores>([\s\S]*?)<\/scores>/,
                );
                if (scoresMatch && scoresMatch[1]) {
                  return JSON.parse(scoresMatch[1]);
                }
                return null;
              } catch (error) {
                console.error("Error parsing scores:", error);
                return null;
              }
            };

            const scores = extractScores(messageContent);

            // Ta bort scores-sektionen från visningsinnehållet
            messageContent = messageContent.replace(
              /<scores>[\s\S]*?<\/scores>/,
              "",
            );

            if (messageContent.startsWith("```html")) {
              messageContent = messageContent.slice(7, -3).trim();
            } else if (messageContent.startsWith("```")) {
              messageContent = messageContent.slice(3, -3).trim();
            }

            // Uppdatera dokumentet med den nya bedömningen
            const docRef = doc(db, "essays", docId);
            await updateDoc(docRef, {
              results: messageContent,
              scores: scores,
              assessmentStyle: newAssessmentStyle,
              reassessedAt: new Date(),
              inputTokens: responseData.usage.prompt_tokens,
              completionTokens: responseData.usage.completion_tokens,
              totalTokens: responseData.usage.total_tokens,
            });

            // Uppdatera lokalt state
            setEssayData({
              ...essayData,
              results: messageContent,
              scores: scores,
              assessmentStyle: newAssessmentStyle,
            });
            setEditedResults(messageContent);
            setShowReassessment(false);
            setIsReassessing(false);

            showSnackbar(
              "Texten har bedömts om med ny bedömningsstil!",
              "success",
            );

            // Rensa upp
            unsubscribe();
          } catch (error) {
            console.error("Error processing response:", error);
            showSnackbar(
              "Ett fel uppstod vid bearbetning av den nya bedömningen",
              "error",
            );
            setIsReassessing(false);
            unsubscribe();
          }
        } else if (data.status === "error") {
          console.error("Error reassessing essay:", data.error);
          showSnackbar(
            `Fel vid ny bedömning: ${data.error || "Ett oväntat fel uppstod."}`,
            "error",
          );
          setIsReassessing(false);
          unsubscribe();
        }
      });

      Logger.log("Reassessment prompt: ", prompt);

      // Gör API-anropet
      const API_URL =
        process.env.NODE_ENV === "production"
          ? "/api"
          : "http://localhost:3001/api";

      fetch(`${API_URL}/openai-essay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          requestId: requestId,
          userId: user.uid,
          prompt: prompt,
          existingDocId: docId,
        }),
      }).catch((error) => {
        console.error("Error initiating API request:", error);
        setIsReassessing(false);
        showSnackbar("Ett fel uppstod vid initiering av ny bedömning", "error");
      });

      // Timeout för att förhindra hängning
      setTimeout(() => {
        unsubscribe();
        if (isReassessing) {
          setIsReassessing(false);
          showSnackbar("Förfrågan tog för lång tid", "error");
        }
      }, 180000);
    } catch (error) {
      console.error("Error during reassessment:", error);
      showSnackbar("Ett fel uppstod vid ny bedömning", "error");
      setIsReassessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    setLoadingDelete(true);
    if (docId === "Fd0Tga66MLf1TsRv1xid") {
      showSnackbar("Demo-bedömning kan inte raderas.", "warning");
      setShowDeleteConfirmation(false);
      return;
    }

    try {
      const docRef = doc(db, "essays", docId);
      await deleteDoc(docRef);
      showSnackbar("Bedömning raderad.", "success");
      setShowDeleteConfirmation(false);
      setLoadingDelete(false);
      navigate("/app/uppsatser");
    } catch (error) {
      setLoadingDelete(false);
      console.error("Error deleting essay:", error);
      showSnackbar("Ett fel uppstod vid radering.", "error");
      setShowDeleteConfirmation(false);
    }
  };

  const [historicEssays, setHistoricEssays] = useState([]);
  const handleSaveAssessment = async (newScores) => {
    if (docId === "Fd0Tga66MLf1TsRv1xid") {
      showSnackbar("Betyget uppdaterat!", "success");
      return;
    }

    try {
      const docRef = doc(db, "essays", docId);
      await updateDoc(docRef, { scores: newScores });
      setEssayData({ ...essayData, scores: newScores });
      showSnackbar("Betyg sparade.", "success");
    } catch (error) {
      console.error("Error updating scores:", error);
      showSnackbar("Ett fel uppstod vid sparande av betyg.", "error");
    }
  };
  const [className, setClassName] = useState("");

  // Add this useEffect to fetch the class name when essayData is loaded
  useEffect(() => {
    const fetchClassName = async () => {
      if (essayData?.classId) {
        try {
          const classDocRef = doc(db, "classes", essayData.classId);
          const classDocSnap = await getDoc(classDocRef);
          if (classDocSnap.exists()) {
            setClassName(classDocSnap.data().name);
          }
        } catch (error) {
          console.error("Error fetching class name:", error);
        }
      }
    };

    fetchClassName();
  }, [essayData?.classId]);
  useEffect(() => {
    const fetchEssayData = async () => {
      try {
        const docRef = doc(db, "essays", docId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setEssayData(data);
          setEditedResults(data.results);
          setHasChanges(false);
          setReviewed(data.reviewed || false);
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching essay data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchHistoricEssays = async () => {
      try {
        const q = query(
          collection(db, "essays"),
          where("userId", "==", user.uid),
          orderBy("createdAt", "desc"), // Add this line to sort by createdAt in descending order
        );
        const querySnapshot = await getDocs(q);
        const essays = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setHistoricEssays(essays);
      } catch (error) {
        console.error("Error fetching historic essays:", error);
      }
    };

    fetchEssayData();
    fetchHistoricEssays();
  }, [docId, user.uid]);

  const handleEdit = () => {
    setEditing(true);
  };
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    // Create a sanitized version of the Quill content
    const contentToPrint = editedResults || essayData.results || "";

    printWindow.document.write(`
      <html>
        <head>
          <title>Bedömning av text från Studera.ai</title>
          <style>

  @page {
      size: A4;
      margin: 0cm 0cm 2cm 0cm;
      marks: none;
    }

          html {
      padding: 0cm 2cm 2cm 2cm !important;
      margin: 0 !important;
      margin-top: 2cm !important;
    }

    body { 
      margin: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
      
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; padding: 20px; }
            h1 { color: #2c3e50; }
            .ql-editor { padding: 0; }
            .ql-editor * { font-family: Arial, sans-serif; }
          </style>
        </head>
        <body>
          <h1>Bedömningsresultat</h1>
          <div class="ql-editor">
            ${contentToPrint}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSave = async () => {
    try {
      const docRef = doc(db, "essays", docId);
      await updateDoc(docRef, { results: editedResults });
      setEssayData({ ...essayData, results: editedResults });
      setEditing(false);
      showSnackbar("Ändringar sparade.", "success");
    } catch (error) {
      console.error("Error updating essay data:", error);
    }
  };

  const handleCancel = () => {
    setEditedResults(essayData.results);
    setEditing(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDelete = (deletedId) => {
    setHistoricEssays(historicEssays.filter((essay) => essay.id !== deletedId));
  };

  const handleCopy = async () => {
    const plainText = essayData.results;
    // Convert to HTML if needed, assuming gradingResults contains markdown
    const htmlContent = marked.parse(plainText);

    try {
      // Try to copy with rich text formatting first
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([plainText], { type: "text/plain" }),
          "text/html": new Blob([htmlContent], { type: "text/html" }),
        }),
      ]);
      showSnackbar("Kopierad till urklipp!", "success");
    } catch (err) {
      console.error("Fel vid formaterad kopiering: ", err);

      // Fallback to plain text if rich copy fails
      try {
        await navigator.clipboard.writeText(plainText);
        showSnackbar("Kopierad som vanlig text!", "success");
      } catch (fallbackErr) {
        console.error("Fel vid vanlig kopiering: ", fallbackErr);
        showSnackbar("Kunde inte kopiera texten", "error");
      }
    }
  };

  const handleReviewToggle = async () => {
    try {
      setLoadingReviewState(true);
      const docRef = doc(db, "essays", docId);
      const newReviewedState = !reviewed;
      await updateDoc(docRef, { reviewed: newReviewedState });
      setReviewed(newReviewedState);

      if (newReviewedState) {
        showSnackbar("Omdöme markerat som granskat.", "success");
      } else {
        showSnackbar("Omdöme markerat som icke granskat.", "info");
      }
      setLoadingReviewState(false);
    } catch (error) {
      setLoadingReviewState(false);
      console.error("Error updating review status:", error);
      showSnackbar(
        "Ett fel uppstod vid uppdatering av granskningsstatus.",
        "error",
      );
    }
  };

  const getAssessmentStyleDisplayName = (style) => {
    // Kolla om det är en custom style först
    const customStyle = customAssessmentStyles.find((s) => s.id === style);
    if (customStyle) {
      return customStyle.name;
    }

    // Standard styles
    switch (style) {
      case "encouraging":
        return "Uppmuntrande";
      case "critical":
        return "Kritisk";
      case "extra_encouraging":
        return "Extra uppmuntrande";
      case "extra_critical":
        return "Extra kritisk";
      case "balanced":
      default:
        return "Balanserad";
    }
  };

  const [reassessmentAnchorEl, setReassessmentAnchorEl] = useState(null);
  const [showReassessmentDialog, setShowReassessmentDialog] = useState(false);

  const toggleOriginalText = () => {
    setShowOriginalText(!showOriginalText);
  };
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!essayData) {
    return <Typography>No essay data found.</Typography>;
  }

  return (
    <Box sx={{ ...styles.container }}>
      <Box sx={styles.innerContainer}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ mr: 1, ml: { xs: 5, md: 0 }, mt: { xs: 2, md: 1 } }}
          >
            <ArrowBackIcon />
          </IconButton>
          {!isMobile && (
            <Typography
              variant="h1"
              sx={{
                ...styles.header,
                borderBottom: "0px",
                marginTop: "24px",
                fontWeight: 600,
                fontSize: "1.5rem",
              }}
            >
              Bedömning
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            sx={{ ...styles.tightButton }}
            color="primary"
            component={Link}
            startIcon={<AddIcon />}
            to="/app/uppsats/ny"
          >
            Ny bedömning
          </Button>

          <Button
            variant="purp"
            onClick={() => setDrawerOpen(true)}
            startIcon={<SegmentIcon />}
            sx={{ ...styles.tightButton, display: "none" }}
          >
            Mina bedömningar
          </Button>
        </Box>

        <Box sx={{ mt: 2, display: "none" }}>
          <Typography sx={{ ...styles.grayText }}>
            <Link
              to="/app/uppsatser"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Bedömningar
            </Link>{" "}
            &nbsp; / &nbsp; {essayData.fileName}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "16px",
            marginBottom: "16px",
            display: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              ...styles.grayText,
            }}
          >
            <SchoolIcon sx={{ fontSize: "18px", mr: 0.3 }} />
            <Typography
              variant="body2"
              sx={{ ...styles.grayText, fontWeight: "500" }}
            >
              {essayData.educationLevel}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              ...styles.grayText,
            }}
          >
            <HistoryEduIcon sx={{ fontSize: "18px", mr: 0.3 }} />
            <Typography
              variant="body2"
              sx={{ ...styles.grayText, fontWeight: "500" }}
            >
              {essayData.rubricName}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              ...styles.grayText,
            }}
          >
            <LanguageIcon sx={{ fontSize: "18px", mr: 0.3 }} />
            <Typography
              variant="body2"
              sx={{ ...styles.grayText, fontWeight: "500" }}
            >
              {essayData.language}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              ...styles.grayText,
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: "18px", mr: 0.3 }} />
            <Typography
              variant="body2"
              sx={{ ...styles.grayText, fontWeight: "500" }}
            >
              {essayData.createdAt.toDate().toLocaleString()}
            </Typography>
          </Box>
        </Box>
        {essayData.scores && (
          <>
            <Box
              sx={{
                display: "none",
                alignItems: "center",

                mb: 2,
                mt: 1,
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Chip
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonOutlineIcon sx={{ fontSize: 14 }} />
                    {essayData.fileName}
                  </Box>
                }
                size="small"
                sx={{
                  ...styles.grayChip,
                  height: "24px",

                  ml: 0,
                }}
              />

              <Chip
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TableChartIcon sx={{ fontSize: 14 }} />
                    {essayData.rubricName} (Matris)
                  </Box>
                }
                size="small"
                sx={{
                  ...styles.grayChip,
                  height: "24px",

                  ml: 2,
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
              }}
            >
              <Box
                sx={{
                  flex: "1 1 auto",
                }}
              >
                <AssessmentDisplay
                  scores={essayData.scores}
                  styles={styles}
                  onSave={handleSaveAssessment}
                />
              </Box>
              {!isMobile && (
                <Box
                  sx={{
                    ...styles.freshBox,
                    width: { xs: "100%", md: "295px" },
                    flexShrink: 0,
                    padding: "10px",
                    display: "flex",
                    flexDirection: "column",
                    height: "285px",
                    mb: { xs: 5, md: 0 },
                    borderRadius: 2,
                  }}
                >
                  {" "}
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "text.secondary",
                      mb: 2.6,
                      mt: 1,

                      letterSpacing: "0.5px",
                      textAlign: "center",
                    }}
                  >
                    OM BEDÖMNINGEN
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sx={{ display: "none" }}>
                      <Box
                        sx={{
                          backgroundColor: (theme) =>
                            `${alpha(theme.palette.divider, 0.03)}`,
                          borderRadius: 2,
                          p: 1.5,
                          height: "67px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          BEDÖMD
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            fontSize: "0.82rem",
                          }}
                        >
                          {essayData.createdAt?.toDate().toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          backgroundColor: (theme) =>
                            `${alpha(theme.palette.divider, 0.03)}`,
                          borderRadius: 2,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          BEDÖMNINGSSTIL
                        </Typography>

                        <Tooltip
                          title={
                            getAssessmentStyleDisplayName(
                              essayData.assessmentStyle || "balanced",
                            ).length > 10
                              ? getAssessmentStyleDisplayName(
                                  essayData.assessmentStyle || "balanced",
                                )
                              : ""
                          }
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            {getAssessmentStyleDisplayName(
                              essayData.assessmentStyle || "balanced",
                            ).length > 10
                              ? getAssessmentStyleDisplayName(
                                  essayData.assessmentStyle || "balanced",
                                ).substring(0, 10) + "..."
                              : getAssessmentStyleDisplayName(
                                  essayData.assessmentStyle || "balanced",
                                )}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          backgroundColor: (theme) =>
                            `${alpha(theme.palette.divider, 0.03)}`,
                          borderRadius: 2,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          STATUS
                        </Typography>
                        <Chip
                          size="small"
                          label={reviewed ? "Granskad" : "Ej granskad"}
                          sx={{
                            height: "22px",
                            fontSize: "0.75rem",
                            backgroundColor: reviewed
                              ? "rgba(76, 175, 80, 0.1)"
                              : "rgba(255, 152, 0, 0.1)",
                            color: reviewed ? "success.main" : "warning.main",
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          backgroundColor: (theme) =>
                            `${alpha(theme.palette.divider, 0.03)}`,
                          borderRadius: 2,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          MATRIS
                        </Typography>

                        <Tooltip
                          title={
                            essayData.rubricName &&
                            essayData.rubricName.length > 20
                              ? essayData.rubricName
                              : ""
                          }
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: essayData.isUserRubric
                                ? "primary.main"
                                : "text.secondary",
                              cursor: essayData.isUserRubric
                                ? "pointer"
                                : "default",
                              "&:hover": essayData.isUserRubric
                                ? { opacity: 0.8 }
                                : {},
                            }}
                            onClick={() => {
                              if (essayData.isUserRubric) {
                                navigate(
                                  `/app/matris/visa/${essayData.rubricId}`,
                                );
                              }
                            }}
                          >
                            {essayData.rubricName &&
                            essayData.rubricName.length > 27
                              ? essayData.rubricName.substring(0, 25) + "..."
                              : essayData.rubricName}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box
                        sx={{
                          backgroundColor: (theme) =>
                            `${alpha(theme.palette.divider, 0.03)}`,
                          borderRadius: 2,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          KLASS
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                          }}
                        >
                          {essayData.classId ? (
                            <Box
                              component="span"
                              sx={{
                                color: "primary.main",
                                cursor: "pointer",
                                "&:hover": { opacity: 0.8 },
                              }}
                              onClick={() =>
                                navigate(`/app/klasser/${essayData.classId}`)
                              }
                            >
                              {className || "..."}
                            </Box>
                          ) : (
                            "Ingen"
                          )}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box
                        sx={{
                          backgroundColor: (theme) =>
                            `${alpha(theme.palette.divider, 0.03)}`,
                          borderRadius: 2,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          ÅRSKURS
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                          }}
                        >
                          {beautifyGradeText(essayData.educationLevel)}
                        </Typography>
                      </Box>
                    </Grid>

                    {essayData.wordCount && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.7)",
                            borderRadius: 1,
                            p: 1.5,
                            border: "1px solid rgba(0,0,0,0.05)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            ANTAL ORD
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                          >
                            {essayData.wordCount}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
          </>
        )}
        <Box
          sx={{
            ...styles.headlineLessonBox,
            mt: { xs: 3, md: 0 },
            mb: { xs: 0, md: 1 },
            width: "100%",
            textAlign: { xs: "center !important", md: "left !imprtant" },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              ...styles.headlineLessonBoxTitle,
              width: "100%",
              justifyContent: { xs: "center", md: "left" },
              textAlign: { xs: "center !important", md: "left !imprtant" },
            }}
          >
            Detaljerad
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
        >
          <Box
            sx={{
              ...styles.freshBox,
              padding: { xs: "0px", md: "20px" },
              paddingTop: { xs: "0px", md: "10px" },

              flex: "1 1 auto",
            }}
          >
            <Tabs
              value={activeContentTab}
              onChange={(e, newValue) => setActiveContentTab(newValue)}
              sx={{
                mb: 2,

                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.primary.main,
                  height: "1px",
                },
              }}
            >
              <Tab label="Bedömning" />
              <Tab label="Originaltext" />
            </Tabs>

            {activeContentTab === 0 && (
              <Box sx={{ marginTop: 0 }}>
                <Quill
                  value={editedResults}
                  onChange={(newContent) => {
                    setEditedResults(newContent);
                  }}
                  theme={theme}
                />
              </Box>
            )}

            {activeContentTab === 1 && (
              <Box
                sx={{
                  whiteSpace: "pre-wrap",
                  overflowY: "auto",
                  maxHeight: "600px",
                }}
              >
                <Quill value={essayData.content} theme={theme} />
              </Box>
            )}
            <Box sx={{ marginTop: "20px", display: "none" }}>
              <Button
                onClick={toggleOriginalText}
                variant="print"
                startIcon={
                  showOriginalText ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
              >
                {showOriginalText ? "Dölj originaltext" : "Visa originaltext"}
              </Button>
              {showOriginalText && (
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap", marginTop: "10px" }}
                >
                  {essayData.content}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                ...styles.lessonControlPanelNuevo2,
                paddingRight: "20px",
                "& .MuiButton-root": {
                  ...styles.lessonControlPanelNewButtons2,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                Hantera
              </Typography>
              <Button
                variant={reviewed ? "success" : "print"}
                disabled={loadingReviewState}
                startIcon={
                  loadingReviewState ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CheckCircleIcon
                      sx={{
                        color: reviewed ? "green" : "text.secondary",
                      }}
                    />
                  )
                }
                onClick={handleReviewToggle}
                sx={{
                  minWidth: { md: "200px" },
                  whiteSpace: "nowrap",
                }}
              >
                {reviewed ? "Granskad" : "Markera som granskad"}
              </Button>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  mt: 3,
                }}
              >
                Ombedöm
              </Typography>
              <Button
                variant="print"
                startIcon={<AutoAwesomeIcon sx={{ color: "#ba9c17" }} />}
                sx={{
                  minWidth: { md: "200px" },
                  whiteSpace: "nowrap",
                }}
                onClick={(event) => {
                  setReassessmentAnchorEl(event.currentTarget);
                  setShowReassessmentDialog(true);
                }}
                disabled={isReassessing}
              >
                Ombedöm med ny stil
              </Button>

              {/* Popover för reassessment */}
              <Popover
                open={showReassessmentDialog}
                anchorEl={reassessmentAnchorEl}
                onClose={() => {
                  if (!isReassessing) {
                    setShowReassessmentDialog(false);
                    setReassessmentAnchorEl(null);
                  }
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                PaperProps={{
                  sx: {
                    width: { xs: "350px", md: "400px" },
                    maxWidth: "95vw",
                    backgroundImage: "none",
                    p: 3,
                    mt: 1,
                    boxShadow: 3,
                    borderRadius: 4,
                  },
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: "text.primary",
                    }}
                  >
                    Välj ny bedömningsstil
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      color: "text.secondary",
                      lineHeight: 1.8,
                      fontSize: "0.9rem",
                    }}
                  >
                    Välj en annan bedömningsstil för att få en ny bedömning av
                    samma text. Detta kommer att ersätta den nuvarande
                    bedömningen.
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <AssessmentStyleSelector
                      assessmentStyle={newAssessmentStyle}
                      setAssessmentStyle={setNewAssessmentStyle}
                      customAssessmentStyles={customAssessmentStyles}
                      setIsCreateStyleDialogOpen={setIsCreateStyleDialogOpen}
                      handleDeleteStyle={handleDeleteStyle}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="print"
                      onClick={() => {
                        setShowReassessmentDialog(false);
                        setReassessmentAnchorEl(null);
                      }}
                      disabled={isReassessing}
                      sx={{
                        minWidth: "80px",
                        ...styles.tightButton,
                        display: "none",
                      }}
                    >
                      Avbryt
                    </Button>
                    <Button
                      variant="purp"
                      onClick={() => {
                        setShowReassessmentDialog(false);
                        setReassessmentAnchorEl(null);
                        handleReassessment();
                      }}
                      disabled={isReassessing}
                      startIcon={<GenerateIcon />}
                      sx={{ minWidth: "120px" }}
                    >
                      Ombedöm
                    </Button>
                  </Box>
                </Box>
              </Popover>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  mt: 3,
                }}
              >
                Spara
              </Typography>
              <Button
                variant="print"
                startIcon={<SaveIcon />}
                sx={{
                  minWidth: { md: "140px" },
                  whiteSpace: "nowrap",
                }}
                onClick={async () => {
                  if (docId === "Fd0Tga66MLf1TsRv1xid") {
                    showSnackbar("Ändringarna sparade.", "success");
                    return;
                  }
                  try {
                    const docRef = doc(db, "essays", docId);
                    await updateDoc(docRef, { results: editedResults });
                    setHasChanges(false);
                    showSnackbar("Ändringar sparade.", "success");
                  } catch (error) {
                    console.error("Error saving changes:", error);
                    showSnackbar("Ett fel uppstod vid sparande.", "error");
                  }
                }}
              >
                Spara ändringar
              </Button>
              <Button
                variant="print"
                startIcon={<ContentCopyIcon />}
                sx={{
                  minWidth: { md: "140px" },
                  whiteSpace: "nowrap",
                }}
                onClick={handleCopy}
              >
                Kopiera
              </Button>
              <Button
                variant="print"
                color="primary"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
                sx={{
                  minWidth: { md: "140px" },
                  whiteSpace: "nowrap",
                }}
              >
                Skriv ut
              </Button>

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  mt: 3,
                }}
              >
                Exportera
              </Typography>
              <ExportButton
                content={editedResults}
                styles={styles}
                bigButton={true}
                sx={{
                  minWidth: { md: "140px" },
                  whiteSpace: "nowrap",
                }}
              />

              {essayData.isUserRubric && (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      mt: 3,
                    }}
                  >
                    Bedömningsmatris
                  </Typography>
                  <Tooltip title="För att motivera din bedömning">
                    <Button
                      variant="print"
                      startIcon={<TableChartIcon />}
                      onClick={() =>
                        navigate(`/app/matris/visa/${essayData.rubricId}`)
                      }
                      sx={{ mb: 1 }}
                    >
                      Visa matris
                    </Button>
                  </Tooltip>
                </>
              )}

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  mt: 3,
                }}
              >
                Radera
              </Typography>

              <Button
                variant="print"
                onClick={() => setShowDeleteConfirmation(true)}
                startIcon={
                  loadingDelete ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon sx={{ color: "#ff0033" }} />
                  )
                }
                sx={{ color: "#ff0033 !important" }}
              >
                Radera bedömning
              </Button>
              <Box>
                <MoreToCome user={user} />
              </Box>
            </Box>
          </Box>
        </Box>
        <EssayListDrawer
          open={drawerOpen}
          historicEssays={historicEssays}
          theme={theme}
          onClose={() => setDrawerOpen(false)}
          onDelete={handleDelete}
        />

        <WelcomeDialogLessons
          lessonType="EssayUpload.js"
          open={true}
          title="Välkommen till bedömningsdelen!"
          imageSrc="https://sportkuriren.se/wp-content/uploads/result.png"
          imageSrcMaxWidth="50%"
          items={[
            {
              icon: "Info",
              text: "Betrakta detta som en preliminär bedömning. Vår AI ger bara ett förslag – det är du som ansvarar för den slutliga bedömningen. Allt enligt AI-förodningen.",
              color: "#ff9e7d",
            },
            {
              icon: "Edit",
              text: "Det står dig fritt att redigera omdömet, justera betyg, omformulera eller ta bort något. Glöm inte att spara.",
              color: "#ff9e7d",
            },

            {
              icon: "RemoveCircle",
              text: "Du kan när som helst radera texterna som du bedömer.",
              color: "#ff6b6b",
            },
            {
              icon: "Security",
              text: "Du väljer själv om du vill att bedömningen ska göras med AI-modeller vars servrar finns inom eller utanför Europa – helt utifrån vad som passar dig bäst.",
              color: "#ff6b6b",
            },
            {
              icon: "SaveAs",
              text: "Kopiera, skriv ut eller exportera omdömet i Word- eller PDF-format",
              color: "#ff9e7d",
            },

            {
              icon: "QueryStats",
              text: "Se samlade resultat över dina klassers omdömen",
              color: "#ff6b6b",
            },
          ]}
        />

        <ConfirmationDialog
          open={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleConfirmDelete}
          title="Ta bort bedömning"
          message="Är du säker på att du vill radera denna bedömning? Detta går inte att ångra."
          confirmText="Ta bort"
          cancelText="Avbryt"
        />

        <LoadingDialog
          open={isReassessing}
          onClose={() => {}} // You can add a function here if needed
          title="Ombedömer text. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          hideProgressBar={true}
          coffee={false}
        />

        <CreateAssessmentStyleDialog
          open={isCreateStyleDialogOpen}
          onClose={() => setIsCreateStyleDialogOpen(false)}
          onStyleCreated={handleStyleCreated}
          userId={user.uid}
        />
      </Box>
    </Box>
  );
};

export default EssayResult;
